import React from 'react'
import { Card, CardContent, CardHeader, Tabs } from '@/shared/ui'
import type { RewardsBingxDailyItem } from '../services/rewardsBingxService'
import type { RewardsAccountExchangeLink, RewardsCycle } from '../types'
import RewardsBingxDailyChartCard from './RewardsBingxDailyChartCard'
import RewardsCycleCard from './RewardsCycleCard'
import RewardsLinksCard from './RewardsLinksCard'

type RewardsTab = 'resumo' | 'grafico' | 'integracao'

type RewardsMemberTabsProps = {
  cycle: RewardsCycle | null
  multiplierX: number
  usdtBrlRate: number
  links: RewardsAccountExchangeLink[]
  bingxLink: RewardsAccountExchangeLink | null
  bingxDaily: RewardsBingxDailyItem[]
  bingxDailyLoading: boolean
  bingxDailyError: string | null
}

function tabFromHash(hash: string): RewardsTab | null {
  const cleaned = String(hash || '').trim().replace('#', '').toLowerCase()
  if (cleaned === 'resumo') return 'resumo'
  if (cleaned === 'grafico') return 'grafico'
  if (cleaned === 'integracao') return 'integracao'
  return null
}

function setHash(tab: RewardsTab) {
  try {
    window.location.hash = tab
  } catch {}
}

export default function RewardsMemberTabs({
  cycle,
  multiplierX,
  usdtBrlRate,
  links,
  bingxLink,
  bingxDaily,
  bingxDailyLoading,
  bingxDailyError,
}: RewardsMemberTabsProps) {
  const isLinked = bingxLink?.status === 'linked'

  const [tab, setTab] = React.useState<RewardsTab>(() => tabFromHash(window.location.hash) ?? 'resumo')

  React.useEffect(() => {
    const onHashChange = () => {
      const next = tabFromHash(window.location.hash)
      if (next) setTab(next)
    }
    window.addEventListener('hashchange', onHashChange)
    onHashChange()
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  React.useEffect(() => {
    if (tab === 'grafico' && !isLinked) {
      setTab('resumo')
      setHash('resumo')
    }
  }, [tab, isLinked])

  return (
    <div id="rewards-tabs">
      <Tabs
        tabs={[
          { value: 'resumo', label: 'Resumo' },
          { value: 'grafico', label: 'Gráfico', disabled: !isLinked },
          { value: 'integracao', label: 'Integração' },
        ]}
        value={tab}
        onValueChange={(v) => {
          const next = v as RewardsTab
          setTab(next)
          setHash(next)
        }}
      >
        {tab === 'resumo' && <RewardsCycleCard cycle={cycle} multiplierX={multiplierX} usdtBrlRate={usdtBrlRate} />}

        {tab === 'grafico' && (
          <div className="space-y-3">
            {bingxDailyError && (
              <div className="rounded-md bg-amber-500/10 p-3 text-sm text-foreground">
                <div className="font-medium text-amber-400">BingX</div>
                <div className="text-foreground/80">{bingxDailyError}</div>
              </div>
            )}

            {bingxDailyLoading ? (
              <Card className="border-0 shadow">
                <CardHeader className="border-b-0 text-base font-semibold">BingX - Gráfico</CardHeader>
                <CardContent className="text-sm text-foreground/70">Carregando dados da BingX...</CardContent>
              </Card>
            ) : isLinked ? (
              <RewardsBingxDailyChartCard items={bingxDaily} multiplierX={multiplierX} usdtBrlRate={usdtBrlRate} />
            ) : (
              <Card className="border-0 shadow">
                <CardHeader className="border-b-0 text-base font-semibold">BingX - Gráfico</CardHeader>
                <CardContent className="text-sm text-foreground/70">Vincule sua conta para visualizar o gráfico do ciclo.</CardContent>
              </Card>
            )}
          </div>
        )}

        {tab === 'integracao' && <RewardsLinksCard links={links} />}
      </Tabs>
    </div>
  )
}

